import React, { useState, useEffect, useCallback } from 'react';
import {
  Briefcase,
  AlertOctagon,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Plus,
  Search,
  Filter,
  Calendar,
  Edit2,
  Trash2,
  RefreshCw,
  User,
  Phone,
  DollarSign,
  X,
  Flame,
  CheckSquare,
  ArrowRightCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { jobMonitorService } from '../../services/jobMonitorService';

const PRIORITIES = [
  {
    id: 'urgent_important',
    label: '1. Urgent & Important',
    shortLabel: 'Urgent & Important',
    badgeBg: 'bg-rose-100 text-rose-800 border-rose-300',
    cardBg: 'bg-gradient-to-br from-rose-50 to-red-100 border-rose-200 text-rose-900',
    icon: Flame,
    desc: 'Do First (Critical / Emergency)',
  },
  {
    id: 'important_not_urgent',
    label: '2. Important but Not Urgent',
    shortLabel: 'Important, Not Urgent',
    badgeBg: 'bg-blue-100 text-blue-800 border-blue-300',
    cardBg: 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200 text-blue-900',
    icon: Calendar,
    desc: 'Schedule (High Value Work)',
  },
  {
    id: 'not_important_urgent',
    label: '3. Not Important but Urgent',
    shortLabel: 'Not Important, Urgent',
    badgeBg: 'bg-amber-100 text-amber-800 border-amber-300',
    cardBg: 'bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-200 text-amber-900',
    icon: Clock,
    desc: 'Delegate / Quick Fix',
  },
  {
    id: 'not_important_not_urgent',
    label: '4. Not Important & Not Urgent',
    shortLabel: 'Low Priority',
    badgeBg: 'bg-slate-100 text-slate-700 border-slate-300',
    cardBg: 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200 text-slate-800',
    icon: CheckSquare,
    desc: 'Low Priority / Backlog',
  },
];

const STATUSES = [
  {
    id: 'pending',
    label: 'Pending',
    badgeBg: 'bg-slate-100 text-slate-700 border-slate-300',
    icon: Clock,
  },
  {
    id: 'in_progress',
    label: 'Work in Progress',
    badgeBg: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    icon: ArrowRightCircle,
  },
  {
    id: 'completed',
    label: 'Completed',
    badgeBg: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    icon: CheckCircle2,
  },
  {
    id: 'completed_unpaid',
    label: 'Completed (Payment Not Received)',
    shortLabel: 'Completed - Unpaid',
    badgeBg: 'bg-amber-100 text-amber-900 border-amber-400 font-bold',
    icon: AlertTriangle,
  },
];

export default function JobMonitor() {
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({
    totalJobs: 0,
    pendingCount: 0,
    inProgressCount: 0,
    completedCount: 0,
    completedUnpaidCount: 0,
    urgentImportantCount: 0,
    importantNotUrgentCount: 0,
    notImportantUrgentCount: 0,
    notImportantNotUrgentCount: 0,
    totalEstimatedFee: 0,
    totalUnpaidFee: 0,
  });
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'urgent_important',
    assignedTo: '',
    customerName: '',
    customerPhone: '',
    estimatedAmount: '',
    paidAmount: '',
    dueDate: '',
    remarks: '',
  });

  // Fetch Jobs Function
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        status: statusFilter,
        priority: priorityFilter,
        search: searchQuery,
      };

      const res = await jobMonitorService.getJobs(params);
      if (res.success) {
        setJobs(res.data || []);
        if (res.summary) setSummary(res.summary);
      }
    } catch (err) {
      console.error('Fetch jobs error:', err);
      toast.error(err?.response?.data?.message || 'Failed to load job monitor records');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, searchQuery]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Open Create Modal
  const handleOpenCreateModal = () => {
    setEditingJob(null);
    setFormData({
      title: '',
      description: '',
      status: 'pending',
      priority: 'urgent_important',
      assignedTo: '',
      customerName: '',
      customerPhone: '',
      estimatedAmount: '',
      paidAmount: '',
      dueDate: '',
      remarks: '',
    });
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title || '',
      description: job.description || '',
      status: job.status || 'pending',
      priority: job.priority || 'urgent_important',
      assignedTo: job.assignedTo || '',
      customerName: job.customerName || '',
      customerPhone: job.customerPhone || '',
      estimatedAmount: job.estimatedAmount !== undefined ? job.estimatedAmount : '',
      paidAmount: job.paidAmount !== undefined ? job.paidAmount : '',
      dueDate: job.dueDate ? new Date(job.dueDate).toISOString().split('T')[0] : '',
      remarks: job.remarks || '',
    });
    setIsModalOpen(true);
  };

  // Handle Quick Status Change
  const handleQuickStatusChange = async (job, newStatus) => {
    try {
      const res = await jobMonitorService.updateJob(job._id, { status: newStatus });
      if (res.success) {
        toast.success(`Job status updated to ${newStatus.replace('_', ' ')}`);
        fetchJobs();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update job status');
    }
  };

  // Handle Submit Form
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error('Please enter a job title');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        estimatedAmount: formData.estimatedAmount !== '' ? Number(formData.estimatedAmount) : 0,
        paidAmount: formData.paidAmount !== '' ? Number(formData.paidAmount) : 0,
      };

      if (editingJob) {
        const res = await jobMonitorService.updateJob(editingJob._id, payload);
        if (res.success) {
          toast.success('Job updated successfully');
          setIsModalOpen(false);
          fetchJobs();
        }
      } else {
        const res = await jobMonitorService.createJob(payload);
        if (res.success) {
          toast.success('New job created successfully');
          setIsModalOpen(false);
          fetchJobs();
        }
      }
    } catch (err) {
      console.error('Save job error:', err);
      toast.error(err?.response?.data?.message || 'Failed to save job record');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Job
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job monitoring record?')) return;

    try {
      const res = await jobMonitorService.deleteJob(id);
      if (res.success) {
        toast.success('Job deleted successfully');
        fetchJobs();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete job');
    }
  };

  // Format Currency
  const formatINR = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  return (
    <div className="space-y-6 antialiased">
      {/* 1. Header & Quick Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600">
            <Briefcase size={26} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Job Monitor & Work Tracker</h1>
            <p className="text-xs text-slate-500 font-medium">
              Daily work monitoring with 4-stage statuses & Eisenhower 4-priority classification
            </p>
          </div>
        </div>

        <button
          onClick={handleOpenCreateModal}
          className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-all duration-150 shrink-0"
        >
          <Plus size={16} />
          <span>Add New Job</span>
        </button>
      </div>

      {/* 2. Eisenhower 4-Priority Matrix Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PRIORITIES.map((p) => {
          const IconComp = p.icon;
          let count = 0;
          if (p.id === 'urgent_important') count = summary.urgentImportantCount;
          else if (p.id === 'important_not_urgent') count = summary.importantNotUrgentCount;
          else if (p.id === 'not_important_urgent') count = summary.notImportantUrgentCount;
          else if (p.id === 'not_important_not_urgent') count = summary.notImportantNotUrgentCount;

          return (
            <div
              key={p.id}
              onClick={() => setPriorityFilter(priorityFilter === p.id ? 'all' : p.id)}
              className={`p-4 rounded-2xl border cursor-pointer transition-all hover:scale-[1.01] ${p.cardBg} ${
                priorityFilter === p.id ? 'ring-2 ring-indigo-500 shadow-md' : 'shadow-xs'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider">{p.shortLabel}</span>
                <IconComp size={18} />
              </div>
              <div className="text-2xl font-black">{count} <span className="text-xs font-medium opacity-80">Jobs</span></div>
              <p className="text-[11px] mt-1 opacity-80 font-medium">{p.desc}</p>
            </div>
          );
        })}
      </div>

      {/* 3. Work Status Summary Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Jobs */}
        <div className="p-4 bg-white rounded-2xl border border-slate-200/80 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Tracked Jobs</span>
            <div className="text-xl font-bold text-slate-900 mt-1">{summary.totalJobs}</div>
          </div>
          <div className="p-2 bg-slate-100 text-slate-700 rounded-xl">
            <Briefcase size={20} />
          </div>
        </div>

        {/* Work In Progress */}
        <div className="p-4 bg-white rounded-2xl border border-slate-200/80 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-bold text-indigo-500 uppercase tracking-wider">Work In Progress</span>
            <div className="text-xl font-bold text-indigo-600 mt-1">{summary.inProgressCount}</div>
          </div>
          <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
            <ArrowRightCircle size={20} />
          </div>
        </div>

        {/* Pending Work */}
        <div className="p-4 bg-white rounded-2xl border border-slate-200/80 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Pending Work</span>
            <div className="text-xl font-bold text-slate-700 mt-1">{summary.pendingCount}</div>
          </div>
          <div className="p-2 bg-slate-100 text-slate-600 rounded-xl">
            <Clock size={20} />
          </div>
        </div>

        {/* Completed & Payment Not Received */}
        <div className="p-4 bg-amber-50/70 border border-amber-200 rounded-2xl shadow-xs flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">Completed (Unpaid)</span>
            <div className="text-xl font-bold text-amber-900 mt-1">
              {summary.completedUnpaidCount} <span className="text-xs font-normal text-amber-700">({formatINR(summary.totalUnpaidFee)})</span>
            </div>
          </div>
          <div className="p-2 bg-amber-200/60 text-amber-900 rounded-xl">
            <AlertTriangle size={20} />
          </div>
        </div>
      </div>

      {/* 4. Filter & Search Toolbar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs space-y-3 md:space-y-0 md:flex md:items-center md:justify-between md:gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search jobs by title, staff, customer name, phone, remarks..."
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 bg-slate-50/50"
          />
        </div>

        {/* Dropdown Filters */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Priority Filter */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-700 bg-white outline-none focus:border-indigo-500 font-medium"
          >
            <option value="all">All Priorities</option>
            {PRIORITIES.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-700 bg-white outline-none focus:border-indigo-500 font-medium"
          >
            <option value="all">All Work Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">Work in Progress</option>
            <option value="completed">Completed (Paid)</option>
            <option value="completed_unpaid">Completed (Payment Not Received)</option>
          </select>

          <button
            onClick={fetchJobs}
            className="p-2 border border-slate-200 hover:bg-slate-50 rounded-xl text-slate-600 transition-colors"
            title="Refresh Data"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* 5. Jobs Data Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200/80">
              <tr>
                <th className="px-5 py-3.5">Priority Matrix</th>
                <th className="px-5 py-3.5">Job Title & Description</th>
                <th className="px-5 py-3.5">Work Status</th>
                <th className="px-5 py-3.5">Assigned Staff</th>
                <th className="px-5 py-3.5">Customer Info</th>
                <th className="px-5 py-3.5">Fee & Payment</th>
                <th className="px-5 py-3.5">Due Date</th>
                <th className="px-5 py-3.5">Remarks</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-5 py-12 text-center text-slate-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw size={24} className="animate-spin text-indigo-600" />
                      <span>Loading job monitoring data...</span>
                    </div>
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-5 py-12 text-center text-slate-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Briefcase size={32} className="text-slate-300" />
                      <p className="font-semibold text-slate-600">No job records found</p>
                      <p className="text-xs text-slate-400">Add a new job or adjust your search filters.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                jobs.map((job) => {
                  const priorityObj =
                    PRIORITIES.find((p) => p.id === job.priority) || PRIORITIES[0];
                  const statusObj =
                    STATUSES.find((s) => s.id === job.status) || STATUSES[0];
                  const unpaidBal = Math.max(0, (job.estimatedAmount || 0) - (job.paidAmount || 0));

                  return (
                    <tr key={job._id} className="hover:bg-slate-50/60 transition-colors">
                      {/* Priority Badge */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span
                          className={`inline-block font-bold text-[10px] px-2.5 py-1 rounded-lg border ${priorityObj.badgeBg}`}
                        >
                          {priorityObj.shortLabel}
                        </span>
                      </td>

                      {/* Job Title & Description */}
                      <td className="px-5 py-3.5 max-w-[220px]">
                        <div className="font-bold text-slate-900">{job.title}</div>
                        {job.description && (
                          <div className="text-[11px] text-slate-500 truncate" title={job.description}>
                            {job.description}
                          </div>
                        )}
                      </td>

                      {/* Work Status (Quick Change Dropdown) */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <select
                          value={job.status}
                          onChange={(e) => handleQuickStatusChange(job, e.target.value)}
                          className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border outline-none cursor-pointer ${statusObj.badgeBg}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Assigned Staff */}
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-600">
                        {job.assignedTo ? (
                          <div className="flex items-center gap-1.5 font-semibold text-slate-800">
                            <User size={13} className="text-slate-400" />
                            <span>{job.assignedTo}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">Unassigned</span>
                        )}
                      </td>

                      {/* Customer Info */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {job.customerName || job.customerPhone ? (
                          <div>
                            <div className="font-bold text-slate-800">{job.customerName || 'Customer'}</div>
                            {job.customerPhone && (
                              <div className="text-[11px] text-slate-500 flex items-center gap-1">
                                <Phone size={11} className="text-slate-400" />
                                <span>{job.customerPhone}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">-</span>
                        )}
                      </td>

                      {/* Fee & Payment */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {job.estimatedAmount > 0 ? (
                          <div>
                            <div className="font-bold text-slate-900">{formatINR(job.estimatedAmount)}</div>
                            {job.status === 'completed_unpaid' || unpaidBal > 0 ? (
                              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                                Unpaid: {formatINR(unpaidBal)}
                              </span>
                            ) : (
                              <span className="text-[10px] text-emerald-600 font-semibold">✓ Paid</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">₹0</span>
                        )}
                      </td>

                      {/* Due Date */}
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500">
                        {job.dueDate
                          ? new Date(job.dueDate).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '-'}
                      </td>

                      {/* Remarks */}
                      <td className="px-5 py-3.5 max-w-[160px] truncate text-slate-500" title={job.remarks}>
                        {job.remarks || <span className="text-slate-300 italic">No notes</span>}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-3.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleOpenEditModal(job)}
                            className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-indigo-600 transition-colors"
                            title="Edit Job"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(job._id)}
                            className="p-1.5 rounded-lg border border-slate-200 hover:bg-rose-50 text-slate-600 hover:text-rose-600 transition-colors"
                            title="Delete Job"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 6. Create / Edit Job Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-xl w-full overflow-hidden max-h-[90vh] flex flex-col animate-scale-up">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
              <h2 className="text-base font-bold text-slate-900">
                {editingJob ? 'Edit Job Record' : 'Add New Job Entry'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form Content */}
            <form onSubmit={handleSubmitForm} className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Job Title */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Job / Work Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. CCTV Camera Installation at Main Store"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 font-medium"
                />
              </div>

              {/* Priority & Work Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Priority Matrix *
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 bg-white font-medium"
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Work Status *
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 bg-white font-medium"
                  >
                    {STATUSES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Assigned Staff & Due Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Assigned Technician / Staff
                  </label>
                  <input
                    type="text"
                    value={formData.assignedTo}
                    onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                    placeholder="e.g. Rahul Sharma"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Target Due Date
                  </label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Customer Name & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Customer / Client Name
                  </label>
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    placeholder="e.g. Ramesh Kumar"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Customer Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.customerPhone}
                    onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                    placeholder="e.g. 9876543210"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Estimated Amount & Paid Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Estimated Job Fee (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={formData.estimatedAmount}
                    onChange={(e) => setFormData({ ...formData, estimatedAmount: e.target.value })}
                    placeholder="e.g. 3500"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Paid Amount (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={formData.paidAmount}
                    onChange={(e) => setFormData({ ...formData, paidAmount: e.target.value })}
                    placeholder="Leave 0 if unpaid"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Work Scope Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Work Description & Scope
                </label>
                <textarea
                  rows="2"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter detailed job requirements or work instructions..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 resize-none"
                />
              </div>

              {/* Work Remarks / Notes */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Progress Remarks / Payment Notes
                </label>
                <textarea
                  rows="2"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="Notes on work progress, pending parts, or payment follow-ups..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 resize-none"
                />
              </div>

              {/* Modal Actions */}
              <div className="pt-3 flex items-center justify-end gap-2 border-t border-slate-100 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-xs transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingJob ? 'Update Job' : 'Create Job'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
